import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { SCOPES } from "./scopes";

/**
 * Edit one item — rename it, caption it, fix its URL, or move it into the scope
 * it should have been filed under.
 */
export default function MediaEditDialog({ item, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [url, setUrl] = useState("");
  const [scope, setScope] = useState("files");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setTitle(item.title || "");
    setCaption(item.caption || "");
    setUrl(item.url || "");
    setScope(item.scope || "files");
    setSaving(false);
  }, [item]);

  if (!item) return null;

  const isFile = item.media_type === "image" || item.media_type === "document";
  const allowedScopes = SCOPES.filter(s =>
    item.media_type === "image" ? s.acceptsImages
      : item.media_type === "document" ? s.acceptsDocuments
      : true
  );

  const submit = async () => {
    setSaving(true);
    const ok = await onSave(item.id, {
      title: title.trim() || item.file_name || "Untitled",
      caption: caption.trim(),
      scope,
      ...(isFile ? {} : { url: url.trim() }),
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="media-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit item</DialogTitle>
          <DialogDescription>
            {isFile ? "Rename, caption or re-file this upload." : "Fix the label, the URL, or which scope it belongs to."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Label</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="media-edit-title" />
          </div>

          {!isFile && (
            <div className="space-y-2">
              <Label>URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="media-edit-url" />
            </div>
          )}

          <div className="space-y-2">
            <Label>Caption <span className="text-muted-foreground font-normal">— shown to sales</span></Label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Rooftop pool, dusk"
              data-testid="media-edit-caption"
            />
          </div>

          <div className="space-y-2">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="media-edit-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {allowedScopes.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="media-edit-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
